import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/LanguageContext";
import { useAuth, formatApiError } from "@/AuthContext";
import { PREF_CATEGORIES, PREF_TEXT_FIELDS } from "@/lib/clientData";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LogOut } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/25 rounded-none px-0 py-3 text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-gold transition-colors";

export default function ClientPortal() {
  const { user } = useAuth();
  return (
    <div className="bg-navy min-h-screen">
      <Navbar />
      {user === null ? (
        <div className="pt-48 pb-32 text-center text-white/60">…</div>
      ) : user ? (
        <Preferences />
      ) : (
        <AuthForms />
      )}
      <Footer />
    </div>
  );
}

/* ---------------- Auth (login / register) ---------------- */
function AuthForms() {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", address: "", email: "", password: "" });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success(isFr ? "Bienvenue." : "Welcome.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pt-40 pb-28 max-w-md mx-auto px-6">
      <div className="flex justify-center items-center gap-4 mb-6">
        <span className="tricolore"><span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" /></span>
        <span className="text-gold text-xs tracking-luxe uppercase">{isFr ? "Espace Client" : "Client Area"}</span>
      </div>
      <h1 className="font-serif-lux text-white text-4xl text-center font-light">
        {mode === "login" ? (isFr ? "Accès Privé" : "Private Access") : (isFr ? "Créer un Compte" : "Create Account")}
      </h1>

      <div className="flex justify-center gap-8 mt-8 mb-10">
        <button data-testid="tab-login" onClick={() => setMode("login")} className={`text-xs tracking-luxe uppercase pb-1 ${mode === "login" ? "text-gold border-b border-gold" : "text-white/50"}`}>
          {isFr ? "Connexion" : "Sign In"}
        </button>
        <button data-testid="tab-register" onClick={() => setMode("register")} className={`text-xs tracking-luxe uppercase pb-1 ${mode === "register" ? "text-gold border-b border-gold" : "text-white/50"}`}>
          {isFr ? "Inscription" : "Register"}
        </button>
      </div>

      <form onSubmit={submit} data-testid="auth-form" className="space-y-6">
        {mode === "register" && (
          <>
            <Field label={isFr ? "Prénom" : "First Name"}><Input data-testid="input-first" value={form.first_name} onChange={upd("first_name")} required className={inputCls} /></Field>
            <Field label={isFr ? "Nom" : "Last Name"}><Input data-testid="input-last" value={form.last_name} onChange={upd("last_name")} required className={inputCls} /></Field>
            <Field label={isFr ? "Adresse" : "Address"}><Input data-testid="input-address" value={form.address} onChange={upd("address")} className={inputCls} /></Field>
          </>
        )}
        <Field label="Email"><Input data-testid="input-email" type="email" value={form.email} onChange={upd("email")} required className={inputCls} /></Field>
        <Field label={isFr ? "Mot de passe" : "Password"}><Input data-testid="input-password" type="password" value={form.password} onChange={upd("password")} required className={inputCls} /></Field>
        <button type="submit" data-testid="auth-submit" disabled={loading} className="btn-lux w-full bg-gold text-navy hover:bg-gold-light py-4 text-xs tracking-luxe uppercase font-medium disabled:opacity-60">
          {loading ? "…" : mode === "login" ? (isFr ? "Se Connecter" : "Sign In") : (isFr ? "Créer mon Accès" : "Create Access")}
        </button>
      </form>
    </section>
  );
}

/* ---------------- Preferences ---------------- */
function Preferences() {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const { user, logout, authAxios } = useAuth();
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPrefs(user?.preferences || {});
  }, [user]);

  const toggle = (key, opt) => {
    setPrefs((p) => {
      const arr = Array.isArray(p[key]) ? p[key] : [];
      return { ...p, [key]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] };
    });
  };
  const setText = (key, val) => setPrefs((p) => ({ ...p, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAxios().post("/client/preferences", prefs);
      toast.success(isFr ? "Vos préférences ont été enregistrées et transmises." : "Your preferences have been saved and sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pt-36 pb-28 max-w-4xl mx-auto px-6 md:px-10">
      <div className="flex justify-between items-start mb-10">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <span className="tricolore"><span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" /></span>
            <span className="text-gold text-xs tracking-luxe uppercase">{isFr ? "Fiche Préférences" : "Preferences Profile"}</span>
          </div>
          <h1 className="font-serif-lux text-white text-4xl font-light">{isFr ? "Bonjour" : "Welcome"}, {user.first_name}</h1>
          <p className="text-white/50 font-light text-sm mt-2">
            {isFr ? "Partagez vos goûts. Nous préparons chaque détail avant votre arrivée." : "Share your tastes. We prepare every detail before you arrive."}
          </p>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-white/60 hover:text-gold text-xs tracking-luxe uppercase">
          <LogOut className="h-4 w-4" /> {isFr ? "Quitter" : "Log out"}
        </button>
      </div>

      <form onSubmit={submit} data-testid="preferences-form" className="space-y-12">
        {PREF_CATEGORIES.map((cat) => (
          <div key={cat.key} data-testid={`pref-${cat.key}`}>
            <h3 className="font-serif-lux text-gold-light text-2xl mb-1">{isFr ? cat.fr : cat.en}</h3>
            <div className="gold-line w-12 mb-5 opacity-50" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {cat.options.map((opt) => {
                const checked = Array.isArray(prefs[cat.key]) && prefs[cat.key].includes(opt);
                return (
                  <label key={opt} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${checked ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(cat.key, opt)} className="accent-[#C9A13A] w-4 h-4" />
                    <span className={`text-sm font-light ${checked ? "text-white" : "text-white/70"}`}>{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
          {PREF_TEXT_FIELDS.map((f) => (
            <div key={f.key} className={f.textarea ? "sm:col-span-2" : ""}>
              <label className="block text-gold/80 text-[11px] tracking-luxe uppercase mb-1">{isFr ? f.fr : f.en}</label>
              {f.textarea ? (
                <Textarea data-testid={`pref-${f.key}`} rows={3} value={prefs[f.key] || ""} onChange={(e) => setText(f.key, e.target.value)} className={`${inputCls} resize-none`} />
              ) : (
                <Input data-testid={`pref-${f.key}`} value={prefs[f.key] || ""} onChange={(e) => setText(f.key, e.target.value)} className={inputCls} />
              )}
            </div>
          ))}
        </div>

        <button type="submit" data-testid="save-preferences" disabled={loading} className="btn-lux bg-gold text-navy hover:bg-gold-light px-10 py-4 text-xs tracking-luxe uppercase font-medium disabled:opacity-60">
          {loading ? "…" : isFr ? "Enregistrer ma Fiche" : "Save My Profile"}
        </button>
      </form>
    </section>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-gold/80 text-[11px] tracking-luxe uppercase mb-1">{label}</label>
    {children}
  </div>
);
